<template>
  <MainLayout max-width="max-w-full">
    <Head title="Builds" />

    <template v-slot:header>Builds</template>

    <div class="mb-4" v-if="$canAny(['builds.write', 'spreadsheets.read'])">
      <ButtonGroup>
        <Link :href="$route('builds.create')" v-if="$can('builds.write')">Add Build</Link>
        <Link :href="exportUrl" v-if="$can('spreadsheets.read')">
          <CloudDownloadIcon class="w-4 h-4 mr-1" /> Export List
        </Link>
        <Link :href="$route('spreadsheets.index')" v-if="$can('spreadsheets.read')">
          <ViewListIcon class="w-4 h-4 mr-1" /> Spreadsheets
        </Link>
        <button v-if="canSyncAll && ! runningBatch && ! sync.hidden" class="-sync" @click="startSync" :disabled="sync.loading">
          <span v-if="sync.loading">Loading ...</span>
          <span v-else>Sync From Production</span>
        </button>
      </ButtonGroup>
    </div>

    <IndexSearch :filters="filters"
                 :options="options"
                 :seat-configuration="seatConfiguration"
                 :status-list="statusList"
                 :show-delete-button="showDeleteButton"/>

    <DataTable :pagination="builds"
               :auto-overflow-x="false"
               class="mt-4">
      <template v-slot:header>
        <th scope="col">
          Vehicle
        </th>
        <th scope="col">
          Part SKU
        </th>
        <th scope="col" class="-wrap" v-for="item in options">
          {{ item.name }}
        </th>
        <th scope="col">
          <span class="sr-only">Edit</span>
        </th>
      </template>

      <tr v-for="build in builds.data" :key="build.id">
        <td>{{ build.vehicle.name }}</td>
        <td>{{ build.part.sku }}</td>
        <td v-for="item in mapOptionValues(build)" class="-wrap">
          {{ item.value }}
        </td>
        <td>
          <div class="flex gap-2 place-content-end">
            <button type="button"
                    class="text-red-600 hover:text-red-900"
                    v-if="$can('builds.write')"
                    @click="deleteBuild(build)">Delete</button>
            <Link :href="$route('builds.edit', [build.id])" class="text-indigo-600 hover:text-indigo-900">Edit</Link>
          </div>
        </td>
      </tr>
    </DataTable>
  </MainLayout>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import PageComponent from '@/pages/page-component'
import DataTable from '@/components/data-table.vue'
import IndexSearch from './index-search.vue'
import { ViewListIcon, CloudDownloadIcon } from '@heroicons/vue/outline'
import UsesPermissions from '@/mixins/uses-permissions'
import LastBatch from '@/mixins/last-batch'

export default {
  mixins: [
    PageComponent,
    UsesPermissions,
    LastBatch,
  ],

  components: {
    DataTable,
    Link,
    IndexSearch,
    ViewListIcon,
    CloudDownloadIcon,
  },

  props: {
    builds: Object,
    options: Array,
    search: String,
    filters: Object,
    exportUrl: String,
    seatConfiguration: Array,
    showDeleteButton: {
      type: Boolean,
      default: false,
    },
    canSyncAll: Boolean,
    statusList: Array,
  },

  data () {
    return {
      lastBatch: this.$page.props.lastImport,
      sync: {
        loading: false,
        hidden: false,
      }
    }
  },

  methods: {
    getYears (build) {
      return [build.start_year, build.end_year]
        .reduce(
          (carry, item) => carry.includes(item)
            ? carry
            : carry.concat(item),
          [],
        )
        .join('-')
    },

    mapOptionValues (build) {
      const bySlug = build.option_values.reduce(
        (carry, { option, ...item }) => ({
          ...carry,
          [option.slug]: item,
        }),
        {}
      )

      return this.options.map(({ slug }) => bySlug[slug] || { value: null })
    },

    deleteBuild (build) {
      if (confirm('Are you sure?')) {
        Inertia.delete(this.$route('builds.destroy', [build.id]))
      }
    },

    startSync () {
      if (! confirm('Syncing all builds will remove existing ones. Are you sure?')) {
        return;
      }

      if (this.sync.loading) {
        return;
      }

      this.sync.loading = true
      Inertia.post(this.$route('sync.start'), {}, {
        onSuccess: () => {
          this.sync.loading = false
          this.sync.hidden = true
        }
      })
    },
  },
}
</script>
